import { X } from "lucide-react";
import { useState } from "react";
import { PLATFORMS, PLATFORM_COLORS } from "../constants";

const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const YEARS = Array.from({ length: 6 }, (_, i) => 2023 + i);

export default function EditModal({ item, onClose, onSave }) {
  const [platform, setPlatform] = useState(item.platform || "");
  const [status, setStatus] = useState(item.status || "pending");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Parsear watched_date existente si la hay
  const existingDate = item.watched_date ? new Date(item.watched_date) : null;
  const [watchedMonth, setWatchedMonth] = useState(
    existingDate ? existingDate.getMonth() + 1 : new Date().getMonth() + 1,
  );
  const [watchedYear, setWatchedYear] = useState(
    existingDate ? existingDate.getFullYear() : new Date().getFullYear(),
  );

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      // Si está marcada como vista, guardar la fecha; si no, limpiarla
      const watched_date =
        status === "watched"
          ? `${watchedYear}-${String(watchedMonth).padStart(2, "0")}-01`
          : null;
      await onSave(item.id, { platform, status, watched_date });
    } catch (err) {
      setError("Error al guardar: " + err.message);
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 modal-overlay flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-cinema-card border border-cinema-border w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag indicator móvil */}
        <div className="w-10 h-1 bg-steel-600 rounded-full mx-auto mt-3 sm:hidden" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-cinema-border">
          <h2 className="text-lg font-bold text-white">Editar</h2>
          <button
            onClick={onClose}
            className="text-steel-400 hover:text-white p-1"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          {/* Preview título */}
          <div className="flex items-center gap-3 mb-5 bg-cinema-bg rounded-xl p-3 border border-cinema-border">
            {item.poster && (
              <img
                src={item.poster}
                alt=""
                className="w-10 h-14 object-cover rounded-lg flex-shrink-0"
              />
            )}
            <div>
              <p className="text-white font-medium text-sm">{item.title}</p>
              <p className="text-steel-400 text-xs">
                {item.year} · {item.type === "series" ? "📺" : "🎬"}
              </p>
            </div>
          </div>

          {/* Plataforma */}
          <label className="block text-sm font-medium text-steel-300 mb-2">
            Plataforma
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-2 gap-2 mb-5">
            <button
              type="button"
              onClick={() => setPlatform("")}
              className={`px-2 py-2 rounded-xl text-xs font-medium transition-all border ${
                !platform
                  ? "border-cinema-accent text-white"
                  : "border-cinema-border text-steel-400"
              }`}
            >
              Ninguna
            </button>
            {PLATFORMS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPlatform(p)}
                className={`flex items-center gap-1.5 px-2 py-2 rounded-xl text-xs font-medium transition-all border ${
                  platform === p
                    ? "border-cinema-accent text-white"
                    : "border-cinema-border text-steel-300 hover:border-steel-500"
                }`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: PLATFORM_COLORS[p] }}
                />
                <span className="truncate">{p}</span>
              </button>
            ))}
          </div>

          {/* Estado */}
          <label className="block text-sm font-medium text-steel-300 mb-2">
            Estado
          </label>
          <div className="flex gap-2 mb-4">
            {[
              { v: "pending", l: "📋 Pendiente" },
              { v: "watched", l: "✅ Vista" },
            ].map((s) => (
              <button
                key={s.v}
                type="button"
                onClick={() => setStatus(s.v)}
                className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all border ${
                  status === s.v
                    ? "border-cinema-accent text-white"
                    : "border-cinema-border text-steel-400"
                }`}
              >
                {s.l}
              </button>
            ))}
          </div>

          {/* ✅ Selector mes/año — solo visible si está marcada como vista */}
          {status === "watched" && (
            <div className="mb-5 bg-cinema-bg border border-cinema-border rounded-xl p-3">
              <label className="block text-xs font-medium text-steel-400 mb-2">
                📅 Mes en que la viste
              </label>
              <div className="flex gap-2">
                <select
                  value={watchedMonth}
                  onChange={(e) => setWatchedMonth(Number(e.target.value))}
                  className="flex-1 bg-cinema-card border border-cinema-border rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-cinema-accent"
                >
                  {MONTHS.map((m, i) => (
                    <option key={i + 1} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  value={watchedYear}
                  onChange={(e) => setWatchedYear(Number(e.target.value))}
                  className="w-24 bg-cinema-card border border-cinema-border rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-cinema-accent"
                >
                  {YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-steel-700 hover:bg-steel-600 text-white font-medium py-2.5 rounded-xl transition-all text-sm"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-cinema-accent hover:bg-cinema-accent-hover disabled:bg-steel-600 text-white font-medium py-2.5 rounded-xl transition-all text-sm"
            >
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
